/**
 * CloudDataDestinationConfigModal — config UI for the `cloud_data_destination`
 * leg-node type (batch bucket file-drop; migration 026).
 *
 * Like CsvDownloadConfigModal, this type has its own modal because its form is
 * unlike the webhook/custom_api mapping form. The user picks a provider
 * (S3 / R2 / GCS), enters the bucket + path + credentials + fields + a daily
 * flush hour, and the server delivers one file per closed org-local day.
 *
 * Credentials are write-only from the browser's perspective: the server sends
 * back a mask (never the ciphertext). Leaving a credential field blank keeps
 * the stored value; typing a new value replaces it.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { getCloudDestRuns, CloudDestRun, getBrainFields } from '../../services/api';
import { BrainField } from '../../types';
import { SYSTEM_FIELDS } from './systemFields';
import ClientAssignmentField from '../shared/ClientAssignmentField';

// Mirrors the backend mask sentinel (legNodeSecrets.CLOUD_CRED_MASK).
const CRED_MASK = '__STORED__';

type Provider = 's3' | 'r2' | 'gcs';
interface FieldRow { name: string; required: boolean; }

interface Props {
  legNode: any;                     // LegNode; .config.cloudDest holds our config
  onSave: (config: any) => void;
  onClose: () => void;
  /** Called after the Client field writes an assignment. */
  onClientAssigned?: () => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const card: React.CSSProperties = {
  background: '#fff', borderRadius: 10, width: 'min(720px, 96vw)',
  maxHeight: '92vh', overflowY: 'auto', padding: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
};
const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', margin: '12px 0 4px' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
const row2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const help: React.CSSProperties = { fontSize: 11, color: '#6b7280', marginTop: 3 };

const CloudDataDestinationConfigModal: React.FC<Props> = ({ legNode, onSave, onClose, onClientAssigned }) => {
  const existing = legNode?.config?.cloudDest ?? {};
  const dataBodyId: string = legNode?.dataBodyId || legNode?.data_body_id || '';

  const [provider, setProvider] = useState<Provider>(existing.provider || 's3');
  const [bucket, setBucket] = useState<string>(existing.bucket || '');
  const [region, setRegion] = useState<string>(existing.region || '');
  const [accountId, setAccountId] = useState<string>(existing.accountId || '');
  const [endpoint, setEndpoint] = useState<string>(existing.endpoint || '');
  const [pathTemplate, setPathTemplate] = useState<string>(existing.pathTemplate || 'inbound/{YYYYMMDD}.ndjson');
  const [format, setFormat] = useState<'ndjson' | 'csv'>(existing.format === 'csv' ? 'csv' : 'ndjson');

  const hasStoredKey = existing?.credentials?.accessKeyId === CRED_MASK;
  const hasStoredSecret = existing?.credentials?.secretAccessKey === CRED_MASK;
  const [accessKeyId, setAccessKeyId] = useState<string>('');
  const [secretAccessKey, setSecretAccessKey] = useState<string>('');

  const [fields, setFields] = useState<FieldRow[]>(
    Array.isArray(existing.fields)
      ? existing.fields.map((f: any) => ({ name: f.name || '', required: !!f.required })).filter((f: FieldRow) => f.name)
      : []
  );

  const [bucketIdMode, setBucketIdMode] = useState<'field' | 'static'>(existing.bucketIdStatic ? 'static' : 'field');
  const [bucketIdField, setBucketIdField] = useState<string>(existing.bucketIdField || '');
  const [bucketIdStatic, setBucketIdStatic] = useState<string>(existing.bucketIdStatic || '');

  const [flushHour, setFlushHour] = useState<number>(Number.isInteger(existing.flushHour) ? existing.flushHour : 1);
  const [writeMode, setWriteMode] = useState<'overwrite' | 'append_suffix'>(existing.writeMode === 'append_suffix' ? 'append_suffix' : 'overwrite');

  const [runs, setRuns] = useState<CloudDestRun[]>([]);
  const [brainFields, setBrainFields] = useState<BrainField[]>([]);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!dataBodyId) {return;}
    getBrainFields(dataBodyId).then(setBrainFields).catch(() => { /* leave empty */ });
    if (legNode?.id) {
      getCloudDestRuns(dataBodyId, legNode.id).then(setRuns).catch(() => { /* history is best-effort */ });
    }
  }, [dataBodyId, legNode?.id]);

  // Brain fields not already selected — offered in the "add field" dropdown.
  const availableToAdd = useMemo(
    () => brainFields.filter(b => !fields.some(f => f.name === b.fieldName)),
    [brainFields, fields]
  );

  const providerHint = useMemo(() => ({
    s3: 'Amazon S3 — IAM access key + secret; set the bucket region.',
    r2: 'Cloudflare R2 — R2 token access key + secret; set the account ID (endpoint host).',
    gcs: 'Google Cloud Storage — HMAC key + secret (Cloud Storage → Settings → Interoperability).',
  }[provider]), [provider]);

  const addFieldByName = (name: string) =>
    setFields(fs => fs.some(f => f.name === name) ? fs : [...fs, { name, required: false }]);
  const removeFieldByName = (name: string) =>
    setFields(fs => fs.filter(f => f.name !== name));
  const setRequiredByName = (name: string, required: boolean) =>
    setFields(fs => fs.map(f => f.name === name ? { ...f, required } : f));

  const handleSave = () => {
    if (!bucket.trim()) { setError('Bucket is required.'); return; }
    if (provider === 'r2' && !accountId.trim() && !endpoint.trim()) { setError('R2 needs an Account ID (or an explicit endpoint).'); return; }
    if (!pathTemplate.trim()) { setError('Path template is required.'); return; }

    const cleanFields = fields.map(f => ({ name: f.name.trim(), required: !!f.required })).filter(f => f.name);

    const cloudDest: any = {
      provider,
      bucket: bucket.trim(),
      region: region.trim() || undefined,
      accountId: accountId.trim() || undefined,
      endpoint: endpoint.trim() || undefined,
      pathTemplate: pathTemplate.trim(),
      format,
      // Blank field => send the mask so the server keeps the stored secret.
      credentials: {
        accessKeyId: accessKeyId || CRED_MASK,
        secretAccessKey: secretAccessKey || CRED_MASK,
      },
      fields: cleanFields,
      flushHour,
      writeMode,
      windowMode: 'prior_local_day',
    };
    if (bucketIdMode === 'static') {cloudDest.bucketIdStatic = bucketIdStatic.trim();}
    else {cloudDest.bucketIdField = bucketIdField.trim();}

    onSave({ ...(legNode.config || {}), cloudDest });
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Cloud Data Destination</h2>
        <p style={{ ...help, marginTop: 4 }}>
          Delivers one file per day of this Brain's leads to a cloud bucket, cut on the account timezone.
        </p>

        {/* Client — deliberately the first thing in the modal. Who a
            destination delivers to is the only decision in here that
            cannot be undone once a lead has gone out. Self-contained:
            it fetches and writes the assignment itself rather than
            riding on this modal's own save flow. */}
        <ClientAssignmentField
          dataBodyId={legNode.dataBodyId}
          legNodeId={legNode.id}
          nodeType={legNode.type}
          onAssigned={onClientAssigned}
        />

        <label style={label}>Provider</label>
        <select style={input} value={provider} onChange={e => setProvider(e.target.value as Provider)}>
          <option value="s3">Amazon S3</option>
          <option value="r2">Cloudflare R2</option>
          <option value="gcs">Google Cloud Storage</option>
        </select>
        <div style={help}>{providerHint}</div>

        <div style={row2}>
          <div>
            <label style={label}>Bucket</label>
            <input style={input} value={bucket} onChange={e => setBucket(e.target.value)} placeholder="my-client-bucket" />
          </div>
          <div>
            {provider === 's3' && (<>
              <label style={label}>Region</label>
              <input style={input} value={region} onChange={e => setRegion(e.target.value)} placeholder="us-east-1" />
            </>)}
            {provider === 'r2' && (<>
              <label style={label}>Account ID</label>
              <input style={input} value={accountId} onChange={e => setAccountId(e.target.value)} placeholder="cloudflare account id" />
            </>)}
            {provider === 'gcs' && (<>
              <label style={label}>Endpoint</label>
              <input style={input} value={endpoint} onChange={e => setEndpoint(e.target.value)} placeholder="https://storage.googleapis.com" />
            </>)}
          </div>
        </div>

        {provider !== 'gcs' && (<>
          <label style={label}>Endpoint (optional override)</label>
          <input style={input} value={endpoint} onChange={e => setEndpoint(e.target.value)} placeholder="leave blank to auto-derive" />
        </>)}

        <div style={row2}>
          <div>
            <label style={label}>Access Key ID</label>
            <input style={input} type="password" autoComplete="off" value={accessKeyId}
              onChange={e => setAccessKeyId(e.target.value)}
              placeholder={hasStoredKey ? '•••• stored — leave blank to keep' : 'access key id'} />
          </div>
          <div>
            <label style={label}>Secret Access Key</label>
            <input style={input} type="password" autoComplete="off" value={secretAccessKey}
              onChange={e => setSecretAccessKey(e.target.value)}
              placeholder={hasStoredSecret ? '•••• stored — leave blank to keep' : 'secret access key'} />
          </div>
        </div>
        <div style={help}>Credentials are encrypted at rest and never shown again after saving.</div>

        <label style={label}>Path template</label>
        <input style={input} value={pathTemplate} onChange={e => setPathTemplate(e.target.value)} />
        <div style={help}>Tokens: {'{YYYYMMDD}'} {'{YYYY-MM-DD}'} {'{YYYY}'} {'{MM}'} {'{DD}'}. Example: inbound/search_traffic/{'{YYYYMMDD}'}.ndjson</div>

        <div style={row2}>
          <div>
            <label style={label}>Format</label>
            <select style={input} value={format} onChange={e => setFormat(e.target.value as any)}>
              <option value="ndjson">Newline-delimited JSON</option>
              <option value="csv">CSV</option>
            </select>
          </div>
          <div>
            <label style={label}>Daily flush hour (account local, 0–23)</label>
            <input style={input} type="number" min={0} max={23} value={flushHour}
              onChange={e => setFlushHour(Math.max(0, Math.min(23, Number(e.target.value) || 0)))} />
          </div>
        </div>

        <label style={label}>Fields (Brain fields to include)</label>
        {fields.length === 0 && <div style={help}>No fields selected yet — add them from the dropdown below.</div>}
        {fields.map((f) => (
          <div key={f.name} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ flex: 1, fontSize: 13, padding: '6px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>{f.name}</span>
            <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={f.required} onChange={e => setRequiredByName(f.name, e.target.checked)} /> required
            </label>
            <button type="button" onClick={() => removeFieldByName(f.name)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}>✕</button>
          </div>
        ))}
        {(() => {
          const availableSystem = SYSTEM_FIELDS.filter(s => !fields.some(f => f.name === s.id));
          return (
            <select style={input} value=""
              disabled={availableToAdd.length === 0 && availableSystem.length === 0}
              onChange={e => { if (e.target.value) {addFieldByName(e.target.value);} }}>
              <option value="">
                {availableToAdd.length === 0 && availableSystem.length === 0 ? '— All fields added —' : '+ Add a field…'}
              </option>
              {availableToAdd.map(b => <option key={b.fieldName} value={b.fieldName}>{b.fieldName}</option>)}
              <optgroup label="System Fields">
                {availableSystem.map(s => <option key={s.id} value={s.id} title={`e.g. ${s.example}`}>{s.label} — {s.example}</option>)}
              </optgroup>
            </select>
          );
        })()}
        <div style={help}>Every row also gets bucket_id, event_date, and event_timestamp automatically. "required" drops rows missing that field.</div>

        <label style={label}>Bucket ID (GEN-/FRM-/BRD-)</label>
        <div style={{ display: 'flex', gap: 16, marginBottom: 6 }}>
          <label style={{ fontSize: 13 }}>
            <input type="radio" checked={bucketIdMode === 'field'} onChange={() => setBucketIdMode('field')} /> From lead field
          </label>
          <label style={{ fontSize: 13 }}>
            <input type="radio" checked={bucketIdMode === 'static'} onChange={() => setBucketIdMode('static')} /> Static value
          </label>
        </div>
        {bucketIdMode === 'field'
          ? <select style={input} value={bucketIdField} onChange={e => setBucketIdField(e.target.value)}>
              <option value="">{brainFields.length === 0 ? '— No Brain fields yet —' : '— Pick a Brain field —'}</option>
              {brainFields.map(b => <option key={b.fieldName} value={b.fieldName}>{b.fieldName}</option>)}
            </select>
          : <input style={input} value={bucketIdStatic} onChange={e => setBucketIdStatic(e.target.value)} placeholder="e.g. GEN-1234" />}

        <label style={label}>Re-run behavior</label>
        <select style={input} value={writeMode} onChange={e => setWriteMode(e.target.value as any)}>
          <option value="overwrite">Overwrite the day's file (idempotent)</option>
          <option value="append_suffix">Write a new suffixed file each run</option>
        </select>

        {runs.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <label style={label}>Recent deliveries</label>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: 160, overflowY: 'auto' }}>
              {runs.map(r => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                  <span>{r.partitionDate}</span>
                  <span style={{ color: r.status === 'completed' ? '#059669' : r.status === 'failed' ? '#dc2626' : '#6b7280' }}>
                    {r.status}{r.status === 'completed' ? ` · ${r.rowCount} rows` : ''}{r.error ? ` · ${r.error.slice(0, 60)}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" onClick={onClose} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={handleSave} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer' }}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default CloudDataDestinationConfigModal;
